import React, { useCallback, useEffect } from 'react';
import {
  Button,
  Card,
  FormGroup,
  H2,
  Intent,
  TextArea,
} from '@blueprintjs/core';

import {
  Gender,
  RacialDenomination,
  WorkflowMeasurements,
  WorkflowPerson,
} from '../../../types';
import { WavelightWorkflowState } from '../types';

import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  resetState,
  setExtraDescriptionForStudy,
  setLoading,
  setMeasurements,
  setPerson,
  setTags,
  setVideo,
  setMetaData,
  updateOximeterModel,
  updatePhoneInfo,
  updatePreassureGauge,
  uploadVideoAndData,
  updateGlucoseExtraInfo,
} from '../reducer';

import FloatingBottom from '../components/FloatingBottom';
import Loading from '../../../components/Loading';
import Uploader from '../components/Uploader';
import PersonData from '../../../components/PersonData';
import Measurements from '../../../components/Measurements';

import { WorkflowContainer } from './styles';
import CustomTagInput from '../components/CustomTagInput';

const Workflow = () => {
  const dispatch = useAppDispatch();
  const state: WavelightWorkflowState = useAppSelector(
    (state) => state.workflow,
  );

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(setMetaData());
    dispatch(setLoading(false));
  }, [dispatch]);

  const onVideoSelected = useCallback(
    ({ target: { files } }: any) => {
      dispatch(setVideo(files[0]));
    },
    [dispatch],
  );

  // Study
  const updateNewTags = useCallback(
    (value: React.ReactNode[]) => {
      const items = value.map((it) => {
        return String(it);
      });
      dispatch(setTags(items));
    },
    [dispatch],
  );

  const updateNewPhoneInfo = useCallback(
    (value: React.ReactNode[]) => {
      const items = value.map((it) => {
        return String(it);
      });
      dispatch(updatePhoneInfo(items));
    },
    [dispatch],
  );

  const updateNewOximeterModel = useCallback(
    (value: React.ReactNode[]) => {
      const items = value.map((it) => {
        return String(it);
      });
      dispatch(updateOximeterModel(items));
    },
    [dispatch],
  );

  const updateNewPreassureGauge = useCallback(
    (value: React.ReactNode[]) => {
      const items = value.map((it) => {
        return String(it);
      });
      dispatch(updatePreassureGauge(items));
    },
    [dispatch],
  );

  const updateNewGlucoseExtraInfo = useCallback(
    (value: React.ReactNode[]) => {
      const items = value.map((it) => {
        return String(it);
      });
      dispatch(updateGlucoseExtraInfo(items));
    },
    [dispatch],
  );

  const setNewExtraDescriptionForStudy = useCallback(
    (value: string) => {
      dispatch(setExtraDescriptionForStudy(value));
    },
    [dispatch],
  );

  // Person
  const updatePerson = useCallback(
    (person: WorkflowPerson) => {
      dispatch(setPerson(person));
    },
    [dispatch],
  );

  // Measurements
  const updateNewMeasurements = useCallback(
    (value: WorkflowMeasurements) => {
      dispatch(setMeasurements(value));
    },
    [dispatch],
  );

  const sendVideo = useCallback(async () => {
    dispatch(setLoading(true));
    await dispatch(
      uploadVideoAndData({
        video: state.data.video as File,
        study: state.data.study,
        person: state.data.person,
        measurements: {
          bpm: state.data.measurements.bpm,
          bloodPreassure: {
            systolic: state.data.measurements.bloodPreassure.systolic,
            diastolic: state.data.measurements.bloodPreassure.diastolic,
          },
          bloodO2Saturation: state.data.measurements.bloodO2Saturation,
          respiratoryRate: state.data.measurements.respiratoryRate,
          glucose: state.data.measurements.glucose,
          ignore: {
            ignoreAll: state.data.measurements.ignore.ignoreAll,
            ignoreBpm: state.data.measurements.ignore.ignoreBpm,
            ignorebloodO2Saturation:
              state.data.measurements.ignore.ignorebloodO2Saturation,
            ignorebloodPreassure:
              state.data.measurements.ignore.ignorebloodPreassure,
            ignoreRespiratoryRate:
              state.data.measurements.ignore.ignoreRespiratoryRate,
            ignoreGlucose: state.data.measurements.ignore.ignoreGlucose,
          },
        },
        metadata: state.data.metadata,
      }),
    );
  }, [dispatch, state]);

  const resetCurrentState = useCallback(() => {
    dispatch(setLoading(true));
    dispatch(resetState());
  }, [dispatch]);

  // Mandatory Fields
  const checkMandatoryFields = useCallback((): undefined | boolean => {
    const bpmCheck =
      state.data.measurements.bpm === 0 &&
      !state.data.measurements.ignore.ignoreBpm;
    const saturationCheck =
      state.data.measurements.bloodO2Saturation === 0 &&
      !state.data.measurements.ignore.ignorebloodO2Saturation;
    const preassureCheck =
      (state.data.measurements.bloodPreassure.diastolic === 0 ||
        state.data.measurements.bloodPreassure.systolic === 0) &&
      !state.data.measurements.ignore.ignorebloodPreassure;
    const respiratoryCheck =
      state.data.measurements.respiratoryRate === 0 &&
      !state.data.measurements.ignore.ignoreRespiratoryRate;
    const glucoseCheck =
      state.data.measurements.glucose === 0 &&
      !state.data.measurements.ignore.ignoreGlucose;

    return (
      state.data.video === null ||
      state.data.person.gender === Gender.UNDEFINED ||
      state.data.person.height === 0 ||
      state.data.person.weight === 0 ||
      state.data.person.age === 0 ||
      state.data.person.racialDenomination === RacialDenomination.UNDEFINED ||
      bpmCheck ||
      saturationCheck ||
      preassureCheck ||
      respiratoryCheck ||
      glucoseCheck
    );
  }, [state.data]);

  if (state.uploadCompleted) {
    return (
      <WorkflowContainer>
        <H2>Uploade Completed!</H2>
        <Button fill onClick={resetCurrentState}>
          Enviar outro video
        </Button>
      </WorkflowContainer>
    );
  }

  if (state.isLoading) {
    return <Loading message="Loading" />;
  }

  return (
    <WorkflowContainer>
      Dados do Estudo:
      <Card>
        <Uploader file={state.data.video} onVideoSelected={onVideoSelected} />
        Para inserir mais de uma tag, separe-as com vírgula
        <br></br>
        <br></br>
        Tags:
        <CustomTagInput
          values={state.data.study.tags || []}
          onChange={updateNewTags}
          onBlur={updateNewTags}
        />
        Informação do aparelho celular:
        <CustomTagInput
          values={state.data.study.phone || []}
          onChange={updateNewPhoneInfo}
          onBlur={updateNewPhoneInfo}
        />
        Informação do oximetro:
        <CustomTagInput
          values={state.data.study.oximeterModel || []}
          onChange={updateNewOximeterModel}
          onBlur={updateNewOximeterModel}
        />
        Informação do medidor de pressão:
        <CustomTagInput
          values={state.data.study.pressureGaugeModel || []}
          onChange={updateNewPreassureGauge}
          onBlur={updateNewPreassureGauge}
        />
        Informação da medição de glicose:
        <CustomTagInput
          values={state.data.study.glucoseExtraInfo || []}
          onChange={updateNewGlucoseExtraInfo}
          onBlur={updateNewGlucoseExtraInfo}
        />
        <FormGroup
          helperText={'Informações extras do estudo...'}
          intent={Intent.PRIMARY}
          label={'Informações extras do estudo'}
          labelFor="extra-study-input"
        >
          <TextArea
            fill
            placeholder="Informações extras do estudo"
            growVertically={true}
            onChange={(e) => setNewExtraDescriptionForStudy(e.target.value)}
            value={state.data.study.extraDescription}
          />
        </FormGroup>
      </Card>
      Dados Pessoais:
      <Card>
        <PersonData person={state.data.person} onChange={updatePerson} />
      </Card>
      Medidas:
      <Card>
        <Measurements
          measurements={state.data.measurements}
          onMeasurementsUpdate={updateNewMeasurements}
        ></Measurements>
      </Card>
      <Card>
        <>
          <FloatingBottom>
            <Button
              fill
              large
              onClick={sendVideo}
              disabled={checkMandatoryFields()}
            >
              Enviar
            </Button>
          </FloatingBottom>
        </>
      </Card>
    </WorkflowContainer>
  );
};

export default Workflow;
